import { createFileRoute } from '@tanstack/react-router';
import {
  Input,
  Select,
  SettingsRow,
  SettingsSection,
  SettingsValue,
  ViewTab,
  ViewTabList,
  useNow,
} from '@wundo/ui';
import { timeZoneOptions } from '@wundo/client/timezones.ts';
import { toSupportedLanguage } from '@wundo/shared/language.ts';
import { ProfilePhotoRow } from '@/components/ProfilePhotoRow.tsx';
import { SaveStatus } from '@/components/SaveStatus.tsx';
import { LANGUAGE_OPTIONS } from '@/lib/languageOptions.ts';
import { useProfileSave } from '@/lib/profileSave.ts';
import { useFeatureFlag } from '@wundo/client/useFeatureFlag.ts';
import { m } from '@/paraglide/messages.js';

export const Route = createFileRoute('/_app/settings/profile')({
  component: ProfileSettingsPage,
});

/* The API bounds a name at 80 characters; stop it at the keyboard rather than at the 422. */
const NAME_MAX_LENGTH = 80;

const THEMES = [
  { value: 'light', label: m.settings_theme_light },
  { value: 'dark', label: m.settings_theme_dark },
  { value: 'auto', label: m.settings_theme_auto },
] as const;

export function ProfileSettingsPage() {
  const { user, save, pending, failed } = useProfileSave();
  const now = useNow();
  const darkModeEnabled = useFeatureFlag('dark_mode');

  if (!user) return null;

  /*
   * The name fields are uncontrolled and keyed off the loaded user, so there is
   * no local copy to keep in sync with the query. Saving on blur keeps the row
   * layout — there is nowhere in a settings row for a per-field save button.
   */
  const saveName =
    (field: 'first_name' | 'last_name') =>
    (event: React.FocusEvent<HTMLInputElement>) => {
      const value = event.target.value.trim();
      const current = user[field] ?? '';

      /*
       * The API requires at least one character for either name, so an emptied
       * field cannot be saved. Put back what is stored rather than leaving the
       * box blank and implying it was cleared.
       */
      if (!value) {
        event.target.value = current;
        return;
      }

      if (value === current) return;

      save(
        { [field]: value },
        {
          /*
           * The selects re-render from the cache, so a failed save undoes itself.
           * These inputs do not — without this the field would keep showing a
           * name the server rejected, which is the same lie as saving silently.
           */
          onError: () => {
            event.target.value = current;
          },
        },
      );
    };

  return (
    <>
      <SaveStatus pending={pending} failed={failed} />
      <SettingsSection title={m.settings_tab_profile()}>
        <ProfilePhotoRow user={user} />
        <SettingsRow label={m.settings_name()}>
          {/* Side by side where there is room; stacked on a phone, where there is not. */}
          <div className='flex gap-2 max-mobile:flex-col'>
            <Input
              key={`first-${user.first_name}`}
              aria-label={m.settings_first_name()}
              defaultValue={user.first_name ?? ''}
              maxLength={NAME_MAX_LENGTH}
              required
              onBlur={saveName('first_name')}
            />
            <Input
              key={`last-${user.last_name}`}
              aria-label={m.settings_last_name()}
              defaultValue={user.last_name ?? ''}
              maxLength={NAME_MAX_LENGTH}
              onBlur={saveName('last_name')}
            />
          </div>
        </SettingsRow>
        <SettingsRow label={m.settings_email()}>
          <SettingsValue>{user.email ?? '—'}</SettingsValue>
        </SettingsRow>
        <SettingsRow
          label={m.settings_language()}
          description={m.settings_language_desc()}
        >
          <Select
            aria-label={m.settings_language()}
            options={LANGUAGE_OPTIONS}
            value={toSupportedLanguage(user.language)}
            onChange={(event) => save({ language: event.target.value })}
          />
        </SettingsRow>
        <SettingsRow label={m.settings_timezone()}>
          <Select
            aria-label={m.settings_timezone()}
            options={timeZoneOptions(user.timezone, now)}
            value={user.timezone}
            onChange={(event) => save({ timezone: event.target.value })}
          />
        </SettingsRow>
        {darkModeEnabled && (
          <SettingsRow label={m.settings_theme()} description={m.settings_theme_desc()}>
            <ViewTabList aria-label={m.settings_theme()}>
              {THEMES.map((option) => (
                <ViewTab
                  key={option.value}
                  active={option.value === user.theme}
                  onClick={() => save({ theme: option.value })}
                >
                  {option.label()}
                </ViewTab>
              ))}
            </ViewTabList>
          </SettingsRow>
        )}
      </SettingsSection>
    </>
  );
}
